import { createContext, type ReactNode, useContext } from "react";

import {
    createVersionedContext,
    createVersionedValueMap,
} from "@backstage/version-bridge";

/**
 * Whether the nav rail is pinned open, and whether the shell is in its mobile
 * layout.
 *
 * Structurally identical to `SidebarPinState` from `@backstage/core-components`
 * — deliberately, because the same value is published to upstream plugins that
 * still read that type. See {@link SidebarPinStateProvider}.
 */
export interface SidebarPinState {
    /**
     * When `false` the rail expands on hover as an overlay; when `true` it
     * stays open and shifts page content.
     */
    isPinned: boolean;
    /** Flips {@link SidebarPinState.isPinned}. */
    toggleSidebarPinState: () => void;
    /** Whether the shell is below the `xs` breakpoint and showing the bottom bar. */
    isMobile?: boolean;
}

/**
 * Matches the default `core-components` falls back to outside a provider, so
 * consumers behave the same whichever context they end up reading.
 */
const DEFAULT_PIN_STATE: SidebarPinState = {
    isPinned: true,
    toggleSidebarPinState: () => {},
    isMobile: false,
};

/**
 * The context this package's own components read. A plain, module-local
 * context — see {@link SidebarPinStateProvider} for why it is not the same one
 * upstream reads.
 */
const SidebarPinStateContext =
    createContext<SidebarPinState>(DEFAULT_PIN_STATE);

/**
 * The context upstream plugins read.
 *
 * `createVersionedContext` is not a normal `createContext`: it stores the
 * context object on `globalThis` under the given key and returns the existing
 * one if it is already there. So this resolves to the *same object*
 * `@backstage/core-components` creates when it runs
 * `createVersionedContext("sidebar-pin-state-context")`
 * (`SidebarPinStateContext.esm.js:12`), without importing that package.
 *
 * That is what lets this module replace the upstream provider. `AppLayout`
 * overrides `app/layout`, so `SidebarPage` — which normally supplies the pin
 * state — never renders, and `@backstage/plugin-search`,
 * `@backstage/plugin-techdocs` and `@backstage/plugin-user-settings` would all
 * silently fall back to `DEFAULT_PIN_STATE`: search would show desktop filters
 * on phones, TechDocs would offset for a sidebar that is not there, and the
 * Settings pin toggle would become a switch that does nothing.
 *
 * The key string and the version number below are internal to
 * `core-components` and are not part of its public API. `sidebarPinState.test.tsx`
 * asserts the round trip against the real upstream hook so a rename fails the
 * build instead of quietly degrading those three plugins.
 */
const UpstreamPinStateContext = createVersionedContext<{
    1: SidebarPinState;
}>("sidebar-pin-state-context");

/**
 * Publishes the pin state to this package and to upstream plugins.
 *
 * Replaces `SidebarPinStateProvider` from `@backstage/core-components`, with
 * the same props, so it is a drop-in at the call site.
 *
 * Two contexts are provided rather than one. Routing our own components
 * through {@link UpstreamPinStateContext} would work, but it would also make
 * the rail's own behaviour depend on an undocumented string: if that key ever
 * changes upstream, only the interop should break, not this sidebar.
 */
export const SidebarPinStateProvider = (props: {
    value: SidebarPinState;
    children: ReactNode;
}) => {
    const { value, children } = props;

    return (
        <SidebarPinStateContext.Provider value={value}>
            <UpstreamPinStateContext.Provider
                value={createVersionedValueMap({ 1: value })}
            >
                {children}
            </UpstreamPinStateContext.Provider>
        </SidebarPinStateContext.Provider>
    );
};

/**
 * Returns the current pin state.
 *
 * Replaces `useSidebarPinState` from `@backstage/core-components`. Defaults to
 * pinned and non-mobile outside a provider, matching that hook.
 */
export const useSidebarPinState = (): SidebarPinState =>
    useContext(SidebarPinStateContext);
